import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getReceipts, getStores, deleteReceipt, deleteReceipts } from '../services/api';
import { format } from 'date-fns';

const EMPTY_FILTERS = { store_id: '', from: '', to: '', min_total: '', max_total: '' };

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <p>{message}</p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

export default function ReceiptsPage() {
  const [data, setData] = useState(null);
  const [stores, setStores] = useState([]);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // { type: 'one'|'bulk', id? }

  const load = useCallback(() => {
    setError('');
    const activeFilters = Object.fromEntries(
      Object.entries(appliedFilters).filter(([, v]) => v !== '')
    );
    getReceipts(page, 20, activeFilters)
      .then((d) => {
        setData(d);
        setSelected(new Set());
      })
      .catch(() => setError('Failed to load receipts'));
  }, [page, appliedFilters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getStores().then(setStores).catch(() => {});
  }, []);

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!data) return;
    setSelected((prev) =>
      prev.size === data.data.length ? new Set() : new Set(data.data.map((r) => r.id))
    );
  };

  const runDelete = async () => {
    const target = confirmTarget;
    setConfirmTarget(null);
    setDeleting(true);
    try {
      if (target.type === 'one') await deleteReceipt(target.id);
      else await deleteReceipts([...selected]);
      load();
    } catch {
      setError(target.type === 'one' ? 'Failed to delete receipt' : 'Failed to delete selected receipts');
    } finally {
      setDeleting(false);
    }
  };

  if (error) return <p className="error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  const { data: receipts, pagination } = data;
  const allSelected = receipts.length > 0 && selected.size === receipts.length;

  return (
    <div>
      {confirmTarget && (
        <ConfirmDialog
          message={
            confirmTarget.type === 'one'
              ? 'Delete this receipt? This cannot be undone.'
              : `Delete ${selected.size} selected receipt(s)? This cannot be undone.`
          }
          onConfirm={runDelete}
          onCancel={() => setConfirmTarget(null)}
        />
      )}

      <div className="page-header">
        <div>
          <h2>Receipts</h2>
          <p className="muted" style={{ margin: 0 }}>{pagination.total} total</p>
        </div>
        <Link to="/upload">
          <button className="btn-primary">+ Upload receipt</button>
        </Link>
      </div>

      <form className="card filter-form" onSubmit={applyFilters} style={{ marginBottom: '1rem' }}>
        <div className="field-store">
          <label htmlFor="filter-store">Store</label>
          <select
            id="filter-store"
            value={filters.store_id}
            onChange={(e) => setFilters((f) => ({ ...f, store_id: e.target.value }))}
          >
            <option value="">All stores</option>
            {stores.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.chain})</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="filter-from">From</label>
          <input
            id="filter-from"
            type="date"
            value={filters.from}
            onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="filter-to">To</label>
          <input
            id="filter-to"
            type="date"
            value={filters.to}
            onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="filter-min">Min total</label>
          <input
            id="filter-min"
            type="number"
            step="0.01"
            min="0"
            value={filters.min_total}
            onChange={(e) => setFilters((f) => ({ ...f, min_total: e.target.value }))}
          />
        </div>
        <div>
          <label htmlFor="filter-max">Max total</label>
          <input
            id="filter-max"
            type="number"
            step="0.01"
            min="0"
            value={filters.max_total}
            onChange={(e) => setFilters((f) => ({ ...f, max_total: e.target.value }))}
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn-secondary">Apply filters</button>
          <button type="button" className="btn-secondary" onClick={clearFilters}>Clear</button>
        </div>
      </form>

      {selected.size > 0 && (
        <div className="card" style={{ marginBottom: '1rem', display: 'flex', gap: 12, alignItems: 'center', padding: '0.6rem 1rem' }}>
          <span>{selected.size} selected</span>
          <button
            className="btn-danger btn-sm"
            disabled={deleting}
            onClick={() => setConfirmTarget({ type: 'bulk' })}
          >
            Delete selected
          </button>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all receipts on this page"
                />
              </th>
              <th>Date</th><th>Store</th><th>Chain</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th style={{ textAlign: 'right' }}>Items</th>
              <th>AI parsed</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {receipts.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem' }}>
                No receipts match. <Link to="/upload">Upload one</Link>.
              </td></tr>
            )}
            {receipts.map((r) => (
              <tr key={r.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggleSelected(r.id)}
                    aria-label={`Select receipt from ${r.purchase_date}`}
                  />
                </td>
                <td>
                  <Link to={`/receipts/${r.id}`} style={{ fontWeight: 500 }}>
                    {r.purchase_date ? format(new Date(r.purchase_date), 'dd MMM yyyy') : '—'}
                  </Link>
                </td>
                <td>{r.store_name || <span className="muted">Unknown</span>}</td>
                <td><span style={{ fontWeight: 500 }}>{r.store_chain || '—'}</span></td>
                <td style={{ textAlign: 'right' }}>{Number(r.total_amount).toFixed(2)} {r.currency}</td>
                <td style={{ textAlign: 'right' }}>{r.item_count}</td>
                <td>{r.ai_parsed ? '✓' : <span className="muted">—</span>}</td>
                <td>
                  <button
                    className="btn-danger btn-sm"
                    disabled={deleting}
                    onClick={() => setConfirmTarget({ type: 'one', id: r.id })}
                    title="Delete receipt"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      {pagination.pages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn-secondary" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
          <span className="muted">Page {pagination.page} of {pagination.pages}</span>
          <button className="btn-secondary" disabled={page === pagination.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
